import React from 'react';
import { Box, Typography, CircularProgress, useMediaQuery, LinearProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useDashboard } from '../../contexts/DashboardContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import CongestionLegend from './CongestionLegend';
import { useColorPalette } from '../../contexts/ColorPaletteContext';
import AnalysisInfoButton from '../ui/AnalysisInfoButton'; // 追加
import WeatherIcon from '../ui/WeatherIcon'; // 追加
import { detectLanguageFromPathname } from '../../i18n';

// 日本語の曜日名に変換する関数
const getDayNameJa = (dayName, isEnglish = false) => {
  if (isEnglish) {
    const enMap = {
      'Monday': 'Mon',
      'Tuesday': 'Tue',
      'Wednesday': 'Wed',
      'Thursday': 'Thu',
      'Friday': 'Fri',
      'Saturday': 'Sat',
      'Sunday': 'Sun'
    };
    return enMap[dayName] || dayName;
  }
  const dayMap = {
    'Monday': '月',
    'Tuesday': '火',
    'Wednesday': '水',
    'Thursday': '木',
    'Friday': '金',
    'Saturday': '土',
    'Sunday': '日'
  };
  return dayMap[dayName] || dayName;
};

// 時間帯別ヒートマップコンポーネント
const TimeHeatmap = () => {
  const location = useLocation();
  const isEnglish = detectLanguageFromPathname(location.pathname) === 'en';
  const { 
    action: selectedAction, 
    year: selectedYear,
    month: selectedMonth,
    location: selectedLocation
  } = useDashboard();
  
  const { 
    calendarData, 
    loading,
    eventData,
    predictionMeta,
  } = useDashboardData();

  const hasPredictionInData = Array.isArray(calendarData)
    && calendarData.some(d => Array.isArray(d.hours) && d.hours.some(h => h?.source === 'prediction'));
  
  // shouldShowCalculationNote関数を直接定義
  const shouldShowCalculationNote = () => {
    return selectedAction?.startsWith('wti') || selectedAction?.startsWith('dti');
  };
  const { getCellColor, getTextColor } = useColorPalette();
  
  // レスポンシブ対応のためのメディアクエリ
  const isMobile = useMediaQuery('(max-width:768px)');
  const isSmallMobile = useMediaQuery('(max-width:480px)');

  // クリックしたセルの参照とポップオーバーの状態管理
  // const [anchorEl, setAnchorEl] = useState(null);
  // const [popperText, setPopperText] = useState('');
  // const [open, setOpen] = useState(false);
  // const cellRefs = useRef({});

  // セルをタップした時の処理
  // const handleCellClick = (day, hour, highlightReason) => {
  //   const cellKey = `${day}-${hour}`;
  //   const cellElement = cellRefs.current[cellKey];
  //   
  //   if (anchorEl === cellElement) {
  //     setOpen(false);
  //     setAnchorEl(null);
  //   } else {
  //     setAnchorEl(cellElement);
  //     setPopperText(highlightReason);
  //     setOpen(true);
  //   }
  // };

  // ポップオーバー以外の場所をクリックした時に閉じる
  // const handleClickAway = () => {
  //   setOpen(false);
  // };


  // このコンポーネントを表示すべきか判断
  const shouldShowComponent = selectedAction && selectedAction.startsWith('wti');
    
  // 表示される場合のみローディングを表示する条件
  const isLoading = shouldShowComponent && loading;

  // このコンポーネントが表示されない場合は何も返さない
  if (!shouldShowComponent) {
    return null;
  }

  // ローディング表示
  if (isLoading) {
    return (
      <Box sx={{ 
        maxWidth: '100%', 
        margin: '0 auto', 
        mt: 2, 
        px: isMobile ? 1 : 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            sx={{ textAlign: isMobile ? 'center' : 'left' }}
          >
            {isEnglish ? 'Day x Time congestion' : '曜日×時間帯の混雑度'}
          </Typography>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2,
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        }}>
          <CircularProgress 
            size={48}
            thickness={4}
            sx={{ color: '#383947' }}
          />
          <Typography variant="h6" color="primary" fontWeight="bold">
            {isEnglish ? 'Loading data...' : 'データを読み込み中...'}
          </Typography>
          <Box sx={{ width: '300px', mt: 1 }}>
            <LinearProgress 
              variant="indeterminate"
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  backgroundColor: '#383947',
                }
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  // calendarDataが空の場合は何も表示しない
  if (!calendarData || calendarData.length === 0) {
    return null;
  }

  // actionが"wti"で始まる場合のみ表示
  if (!selectedAction || !(selectedAction.startsWith('wti') )) {
    return null;
  }

  // データ形式チェック
  let hasValidData = false;
  let isDtiFormat = false;
  
  try {
    // DTIデータ形式かWTIデータ形式かを検出
    if (Array.isArray(calendarData) && calendarData.length > 0) {
      const firstItem = calendarData[0];
      
      if ((firstItem.day || firstItem.date) && Array.isArray(firstItem.hours) && firstItem.hours.length > 0) {
        hasValidData = true;
        
        if (firstItem.date && (!firstItem.day || firstItem.day.includes('-'))) {
          isDtiFormat = true;
        }
      }
    }
  } catch (error) {
    console.error("Error checking data format:", error);
  }

  // DTIデータ形式の場合はコンポーネントを表示しない（DateTimeHeatmapで表示）
  if (isDtiFormat) {
    return null;
  }

  if (!hasValidData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
        <Typography variant="body1" color="error">
          {isEnglish ? 'Invalid data format. Please select another action.' : 'データ形式が正しくありません。別のアクションを選択してください。'}
        </Typography>
      </Box>
    );
  }

  // 時間の範囲を定義（7-22時）に修正
  const hours = Array.from({ length: 16 }, (_, i) => i + 7); // 7時から22時まで

  // 曜日の並び順を調整（日曜始まり）
  const orderedDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  let sortedData = [];
  try {
    sortedData = [...calendarData].sort((a, b) => {
      return orderedDays.indexOf(a.day) - orderedDays.indexOf(b.day);
    });
  } catch (error) {
    console.error("Error sorting data:", error);
    sortedData = calendarData;
  }

  // 場所名の取得（ファイル名部分のみ）
  const getPlaceName = () => {
    if (!selectedLocation) return 'default';
    const parts = selectedLocation.split('/');
    const filename = parts[parts.length - 1];
    return filename.replace('.csv', '');
  };

  // 曜日に基づいてイベント情報を取得（週の範囲内のイベント）
  const getEventsForDay = (dayName) => {
    if (!eventData || !Array.isArray(eventData)) return [];
    if (!selectedYear || !selectedMonth) return [];
    
    try {
      // 曜日名から該当する日付を計算
      const dayMap = {
        'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
        'Thursday': 4, 'Friday': 5, 'Saturday': 6
      };
      
      const targetDay = dayMap[dayName];
      if (targetDay === undefined) return [];
      
      // 選択された月の全ての日付をチェック
      const year = selectedYear;
      const month = selectedMonth;
      const daysInMonth = new Date(year, month, 0).getDate();
      const matchingEvents = [];
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        if (date.getDay() === targetDay) {
          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const dayEvents = eventData.filter(event => event.date === dateStr);
          matchingEvents.push(...dayEvents);
        }
      }
      
      return matchingEvents;
    } catch (error) {
      console.error('Error in getEventsForDay:', error, 'dayName:', dayName);
      return [];
    }
  };

  return (
    // <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ 
        maxWidth: '100%', 
        margin: '0 auto', 
        mt: 2, 
        px: isMobile ? 1 : 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            sx={{ textAlign: isMobile ? 'center' : 'left' }}
          >
            {isEnglish ? 'Day x Time congestion' : '曜日×時間帯の混雑度'}
          </Typography>
          
          {/* 分析情報ボタンを追加 */}
          <AnalysisInfoButton 
            analysisType="weekTime"
            place={getPlaceName()}
          />
        </Box>

        {/* 予測データを含む場合のバナー */}
        {hasPredictionInData && (
          <Box sx={{
            mb: 1,
            px: isMobile ? 1 : 1.2,
            py: isMobile ? 0.6 : 0.8,
            borderRadius: '6px',
            backgroundColor: 'rgba(85, 60, 154, 0.08)',
            border: '1px solid rgba(85, 60, 154, 0.35)',
            color: '#3a2a73',
            fontSize: isMobile ? '11px' : '12px',
            lineHeight: 1.3,
          }}>
            {isEnglish
              ? 'Cells with "Pred" include predicted future days for the selected month.'
              : '「予測」バッジ付きのセルには未来の予測値が反映されています。'}
            {predictionMeta?.prediction_run_date && (
              <span style={{ marginLeft: 6, opacity: 0.75 }}>
                ({isEnglish ? 'as of ' : '予測日 '}
                {predictionMeta.prediction_run_date})
              </span>
            )}
          </Box>
        )}

        <CongestionLegend
          showCalculationNote={shouldShowCalculationNote()}
          legendType="heatmap"
        />
        
        {/* ヘッダーとセルを分離したコンテナ */}
        <Box sx={{ 
          border: '1px solid #ddd', 
          borderRadius: '8px', 
          overflow: 'hidden',
          width: '100%'
        }}>
          {/* スクロール可能なコンテンツエリア */}
          <Box sx={{ 
            display: 'flex',
            position: 'relative',
          }}>
            {/* 曜日ラベル列 - 垂直方向に固定 */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              width: isMobile ? '50px' : '60px', 
              minWidth: isMobile ? '50px' : '60px',
              position: 'sticky',
              left: 0,
              zIndex: 2,
              boxShadow: '2px 0 4px rgba(0,0,0,0.05)'
            }}>
              {/* 左上角（曜日ヘッダー） */}                <Box sx={{ 
                borderRight: '1px solid #ddd',
                borderBottom: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: isMobile ? '5px 2px' : '10px 5px',
                backgroundColor: '#f5f5f5',
                height: isMobile ? (isSmallMobile ? '45px' : '50px') : '50px',
              }}>
                <Typography 
                  variant={isSmallMobile ? "bodyS" : "bodyM"} 
                  fontWeight="bold"
                >
                  {isEnglish ? 'Day' : '曜日'}
                </Typography>
              </Box>

              {/* 曜日ラベル */}
              {sortedData.map((dayData, rowIndex) => {
                const events = getEventsForDay(dayData.day);
                const hasEvents = events.length > 0;
                const baseHeight = isMobile ? (isSmallMobile ? 45 : 50) : 50;
                const eventHeight = hasEvents ? (isSmallMobile ? 12 : 14) : 0;
                const totalHeight = baseHeight + eventHeight;
                
                return (
                  <Box 
                    key={`day-label-${dayData.day}`}
                    sx={{ 
                      borderRight: '1px solid #ddd',
                      borderBottom: rowIndex !== sortedData.length - 1 ? '1px solid #ddd' : 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      padding: isMobile ? '2px 0' : '4px 0',
                      backgroundColor: '#f9f9f9',
                      height: `${totalHeight}px`,
                      position: 'relative',
                      gap: '1px'
                    }}
                  >
                    <Box sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography 
                        variant={isSmallMobile ? "bodyS" : "bodyM"} 
                        fontWeight="bold"
                        sx={{ fontSize: isSmallMobile ? '11px' : '13px' }}
                      >
                        {getDayNameJa(dayData.day, isEnglish)}
                      </Typography>
                    </Box>
                    
                    {/* イベント情報エリア */}
                    {hasEvents && (
                      <Box sx={{
                        width: '100%',
                        minHeight: isSmallMobile ? '12px' : '14px',
                        backgroundColor: 'rgba(255, 255, 255, 0.75)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1px 2px',
                        overflow: 'visible'
                      }}>
                        <Typography 
                          sx={{ 
                            fontSize: isSmallMobile ? '7px' : '8px',
                            color: '#333',
                            fontWeight: '600',
                            lineHeight: '1.1',
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            hyphens: 'auto',
                            maxWidth: '100%'
                          }}
                          title={events.map(e => e.title).join(', ')}
                        >
                          {events.length > 2 ? (isEnglish ? `${events.length} events` : `${events.length}件`) : 
                            events.map((event, index) => (
                              <span key={index}>
                                {event.title.substring(0, 4)}
                                {index < events.length - 1 && <br />}
                              </span>
                            ))
                          }
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* セルとヘッダーのスクロール可能なエリア */}
            <Box sx={{ 
              overflowX: 'auto',
              overflowY: 'hidden', // 'auto'から'hidden'に変更
              WebkitOverflowScrolling: 'touch',
              zIndex: 1,
              '&::-webkit-scrollbar': {
                height: '8px',
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: '4px'
              }
            }}>
              {/* 時間帯ヘッダー */}
              <Box sx={{ 
                display: 'flex',
                backgroundColor: '#f5f5f5',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
              }}>
                {hours.map(hour => (
                  <Box 
                    key={`hour-${hour}`} 
                    sx={{ 
                      minWidth: isMobile ? (isSmallMobile ? '30px' : '40px') : '40px',
                      width: isMobile ? (isSmallMobile ? '30px' : '40px') : '40px',
                      textAlign: 'center', 
                      padding: isMobile ? '4px 2px' : '6px 2px',
                      borderRight: hour !== hours[hours.length - 1] ? '1px solid #ddd' : 'none',
                      borderBottom: '1px solid #ddd',
                      flexShrink: 0,
                      height: isMobile ? (isSmallMobile ? '45px' : '50px') : '50px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography 
                      variant={isSmallMobile ? "bodyS" : "bodyM"}
                      fontWeight="bold"
                    >
                      {hour}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* 時間ごとのセル（行） */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {sortedData.map((dayData, rowIndex) => {
                  const events = getEventsForDay(dayData.day);
                  const hasEvents = events.length > 0;
                  const baseHeight = isMobile ? (isSmallMobile ? 45 : 50) : 50;
                  const eventHeight = hasEvents ? (isSmallMobile ? 12 : 14) : 0;
                  const totalHeight = baseHeight + eventHeight;
                  
                  return (
                    <Box 
                      key={`day-row-${dayData.day}`} 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'row'
                      }}
                    >
                    {/* 時間ごとのセル */}
                    {hours.map((hour, colIndex) => {
                      const hourData = dayData.hours.find(h => h.hour === hour);
                      const congestion = hourData ? hourData.congestion : 0;
                      // const highlighted = hourData && hourData.highlighted;
                      // const highlightReason = hourData ? hourData.highlight_reason : '';
                      // const cellKey = `${dayData.day}-${hour}`;
                      
                      return (
                        <Box 
                          key={`${dayData.day}-${hour}`}
                          // ref={(el) => cellRefs.current[cellKey] = el}
                          // onClick={() => highlighted && highlightReason ? 
                          //   handleCellClick(dayData.day, hour, highlightReason) : null}
                          sx={{ 
                            minWidth: isMobile ? (isSmallMobile ? '30px' : '40px') : '40px',
                            width: isMobile ? (isSmallMobile ? '30px' : '40px') : '40px',
                            height: `${totalHeight}px`,
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: congestion === 0 ? '#e0e0e0' : getCellColor(congestion),
                            borderRight: colIndex !== hours.length - 1 ? '1px solid #ddd' : 'none',
                            borderBottom: rowIndex !== sortedData.length - 1 ? '1px solid #ddd' : 'none',
                            position: 'relative',
                            // cursor: highlighted ? 'pointer' : 'default',
                            cursor: 'default',
                            flexShrink: 0,
                          }}
                          // title={`${getDayNameJa(dayData.day)} ${hour}時 ${congestion === 0 ? '(データなし)' : `(混雑度: ${congestion})`}`}
                        >
                          {/* メインコンテンツエリア */}
                          <Box sx={{ 
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: congestion === 0 ? '#666' : getTextColor(congestion),
                          }}>
                            <Typography 
                              sx={{ 
                                fontSize: isMobile ? (isSmallMobile ? '18px' : '20px') : '22px',
                                lineHeight: '1',
                                fontWeight: 'bold',
                                textAlign: 'center'
                              }}
                            >
                              {congestion === 0 ? '-' : congestion}
                            </Typography>
                          </Box>
                          
                          {/* 天気情報エリア（右上に配置） */}
                          {hourData && hourData.weather_info && (
                            <Box sx={{
                              position: 'absolute',
                              top: '1px',
                              right: '1px',
                              width: isSmallMobile ? '12px' : '14px',
                              height: isSmallMobile ? '12px' : '14px',
                              backgroundColor: 'rgba(255, 255, 255, 0.85)',
                              borderRadius: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}>
                              <WeatherIcon 
                                weather={hourData.weather_info.weather}
                                size="small"
                                showTemp={false}
                              />
                            </Box>
                          )}

                          {/* 予測バッジ（右下） */}
                          {hourData && hourData.source === 'prediction' && (
                            <Box sx={{
                              position: 'absolute',
                              bottom: '1px',
                              right: '1px',
                              backgroundColor: 'rgba(85, 60, 154, 0.92)',
                              color: '#fff',
                              borderRadius: '3px',
                              padding: '0 2px',
                              fontSize: isSmallMobile ? '7px' : '8px',
                              fontWeight: 'bold',
                              letterSpacing: '0.02em',
                              zIndex: 3,
                              lineHeight: 1.1,
                              pointerEvents: 'none',
                            }}>
                              {isEnglish ? 'Pred' : '予測'}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ポッパー */}
        {/*
        <Popper 
          open={open} 
          anchorEl={anchorEl}
          placement={isMobile ? "bottom" : "top"}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, isMobile ? 5 : 10],
              },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: document.body,
              },
            },
          ]}
          sx={{ 
            zIndex: 1500
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: isMobile ? 1.5 : 2, 
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ddd',
              maxWidth: isMobile ? '180px' : '200px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              fontSize: isMobile ? (isSmallMobile ? '12px' : '14px') : undefined
            }}
          >
            <Typography variant={isMobile ? "bodyS" : "bodyM"} fontWeight="bold">
              {popperText}
            </Typography>
          </Paper>
        </Popper>
        */}

      </Box>
    // </ClickAwayListener>
  );
};

export default TimeHeatmap;
